import { useState } from 'react';
import { Button, Card, Checkbox, Typography, Space, Row, Col } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { useCustomMutation, useNotification } from '@refinedev/core';
import { CACHE_SCOPES as SCOPES } from '../utils/cacheScopes';

/**
 * Manual cache control for shared reference lists. The server invalidates
 * automatically on CMS writes and via TTL — per-scope/all clearing here covers
 * out-of-band changes (DB reseed, import scripts run outside the API process).
 */
export const SystemCache = () => {
  const { mutate, isLoading } = useCustomMutation();
  const { open } = useNotification();
  const [selected, setSelected] = useState<string[]>(SCOPES.map((s) => s.value));
  const [clearedAt, setClearedAt] = useState<string | null>(null);

  const handleClear = () => {
    if (selected.length === 0) return;
    mutate(
      {
        url: '/exercise-admin/cache/clear',
        method: 'post',
        values: { scopes: selected },
      },
      {
        onSuccess: (data) => {
          setClearedAt(new Date().toLocaleTimeString());
          const cleared =
            (data as { cleared?: string[] } | undefined)?.cleared ?? selected;
          open?.({
            type: 'success',
            message: 'Caches cleared',
            description: `Cleared: ${cleared.join(', ')}`,
          });
        },
        onError: (error) => {
          open?.({
            type: 'error',
            message: 'Failed to clear caches',
            description: String(error),
          });
        },
      },
    );
  };

  return (
    <Card title="Shared List Caches" style={{ margin: 16 }}>
      <Typography.Paragraph type="secondary">
        The API invalidates these caches automatically on CMS writes and via
        TTL. Use this after out-of-band changes (DB reseed, import scripts run
        outside the API process). Select scopes to clear — or keep all checked
        to clear everything.
      </Typography.Paragraph>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[16, 8]}>
          {SCOPES.map((scope) => (
            <Col key={scope.value} span={8}>
              <Card
                size="small"
                style={{
                  background: selected.includes(scope.value)
                    ? '#f6ffed'
                    : undefined,
                }}
              >
                <Checkbox
                  checked={selected.includes(scope.value)}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked
                        ? [...prev, scope.value]
                        : prev.filter((v) => v !== scope.value),
                    )
                  }
                >
                  <Typography.Text strong>{scope.label}</Typography.Text>
                </Checkbox>
                <Typography.Paragraph
                  type="secondary"
                  style={{ marginBottom: 0, fontSize: 12, marginTop: 4 }}
                >
                  {scope.description}
                </Typography.Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
        <Button
          type="primary"
          icon={<ClearOutlined />}
          loading={isLoading}
          disabled={selected.length === 0}
          onClick={handleClear}
        >
          Clear Selected ({selected.length})
        </Button>
        {clearedAt && (
          <Typography.Text type="secondary">
            Last cleared at {clearedAt}
          </Typography.Text>
        )}
      </Space>
    </Card>
  );
};
